<template>
  <div>

    <TitlePage :title="$t('bookings')"></TitlePage>

    <v-row class="align-center">
      <v-col cols="12" md="12">
        <ItemActions :selected="selected" @message="actionHandler" :delete-permission="getUser.super_admin"></ItemActions>
      </v-col>
    </v-row>

    <v-card tile class="filter-card">
      <!-- TOOLBAR SUPERIORE -->
      <v-toolbar flat dense class="">
        <v-toolbar-title class="subtitle-2 font-weight-bold">
          {{ $t('filterResults') }}
        </v-toolbar-title>
        <v-spacer />

        <!-- Reset hard -->
        <v-btn small color="error" @click="hardReset" class="mb-2">
          <v-icon left small>mdi-restore</v-icon> RESET
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-row class="px-4 pt-3 pb-1 align-center">
        <v-col cols="12" md="3" v-if="isSuperAdmin">
          <CenterSelectSearch
              v-model="filter.center_id"
              :multiple="false"
              :includeAllOption="true"
              autoStrategy="user"
              :returnObject="false"
              @message="setCenterId"
          />
        </v-col>

        <v-col cols="12" md="2" v-if="isSuperAdmin">
          <v-select
              :items="promoItems"
              v-model="filter.promo_sa_global"
              item-text="text" item-value="value"
              label="Promo SA"
              clearable dense outlined hide-details
          />
        </v-col>

        <v-col cols="12" :md="isSuperAdmin ? 3 : 6">
          <v-text-field
              v-model="filter.customer_q"
              :label="$t('customer') + ' (Nome/Cognome)'"
              clearable dense outlined hide-details
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-text-field
              v-model="filter.code"
              :label="$t('code') + ' ' + $t('booking')"
              clearable dense outlined hide-details
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-select
              v-model="filter.status"
              :items="statuses"
              item-text="text" item-value="value"
              label="Stato"
              clearable dense outlined hide-details
          />
        </v-col>

        <v-col cols="12" md="2">
          <SportsSelectSearch
              v-model="filter.sport_id"
              :multiple="false"
              :returnObject="false"
              :includeAllOption="true"
              @message="(id) => { filter.sport_id = id }"
          />
        </v-col>

        <v-col cols="12" md="5">
          <DateRangeQuick
              v-model="bookingFilter"
              :label="$t('date')"
              @change="onBookingChange"
          />
        </v-col>

        <v-col cols="12" md="5">
          <DateRangeQuick
              v-model="createdFilter"
              :label="$t('creation_date')"
              @change="onCreatedChange"
          />
        </v-col>
      </v-row>
    </v-card>

    <div class="mt-5">
      <div style="text-align: right;font-size: 11px;font-weight: 600;padding-bottom: 5px;">
        <span>{{(items_count) ? items_count:0}} risultati trovati</span>
      </div>

      <v-data-table
          v-model="selected" :headers="headers" :items="items"
          :single-select="false" item-key="_id" show-select class="elevation-1"
          disable-pagination hide-default-footer
      >

        <template v-slot:item.code="{ item }">
          <router-link :to="pathEdit + item._id"><strong>{{ item.code }}</strong></router-link>
        </template>

        <template v-slot:item.customer="{ item }">
          <template v-if="item.customer[0]">
            <template v-if="isSuperAdmin">
              <router-link
                  v-if="item.customer[0]._id"
                  :to="`/customers/details/${item.customer[0]._id}`"
                  target="_blank"
                  rel="noopener"
              >
                <strong>{{ item.customer[0].name }} {{ item.customer[0].surname }}</strong>
              </router-link>
            </template>
            <template v-else>
              {{ item.customer[0].name }} {{ item.customer[0].surname }}
            </template>
          </template>
          <template v-else-if="item.note">
            {{item.note}}
          </template>
          <template v-else>
            N.D.
          </template>
        </template>

        <template v-slot:item.center="{ item }">
          <template v-if="item.center[0]">
            <template v-if="isSuperAdmin">
              <router-link target="_blank" rel="noopener"
                           v-if="item.center[0]._id"
                           :to="`/centers?_id=${item.center[0]._id}`"
              >
                {{ item.center[0].name }}
              </router-link>
            </template>
            <template v-else>
              {{ item.center[0].name }}
            </template>
          </template>
          <template v-else>
            N.D.
          </template>
        </template>

        <template v-slot:item.field="{ item }">
          <template v-if="item.field[0]">
            <router-link target="_blank" rel="noopener"
                v-if="item.field[0]._id"
                :to="`/fields/settings/${item.field[0]._id}`"
            >
              {{ item.field[0].name }}
            </router-link>
          </template>
          <template v-else>
            N.D.
          </template>
        </template>

        <template v-slot:item.sport="{ item }">
          {{ item.sport[0].name }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="getStatus(item.status, 'color')">{{ getStatus(item.status, 'name') }}</v-chip>
        </template>

        <template v-slot:item.price="{ item }">
          {{ item.price.toFixed(2) }} €
        </template>

        <template v-slot:item.date="{ item }">
          {{ getSlot(item.slot_id) }}
        </template>

        <template v-slot:item.promo_sa_global="{ item }">
          <template v-if="isSuperAdmin">
            {{ (item.promo_sa_global || item.promo_sa) ? 'SI': 'NO'}}
          </template>
          <template v-else>

          </template>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ new Date(item.created_at).toLocaleString() }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-menu bottom left>

            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" color="primary">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item @click="edit(item._id)" :title="$t('edit')" dense>
                <v-list-item-title>
                  <v-icon dense color="primary" class="mr-3">mdi-file-edit</v-icon>
                  Visualizza/Gestisci
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="confirmRemove(item._id)" :title="$t('delete')" v-if="isSuperAdmin">
                <v-list-item-title>
                  <v-icon dense color="primary" class="mr-3">mdi-delete</v-icon>
                  {{ $t('delete') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>
      <div class="text-center pt-3 pb-3">
        <v-row>
          <v-col cols="12" md="10" style="text-align: end">
            <v-pagination
                v-model="options_pagination.page"
                :length="calculatePagination(items_count,options_pagination.limit)"
                :total-visible="10"
                @input="getNewPage()"
            ></v-pagination>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
                v-model="options_pagination.limit"
                :items="items_for_page"
                label="Risultati per pagina"
                dense hide-details small-chips outlined @change="makeSearch(true)"/>
          </v-col>
        </v-row>
      </div>

    </div>

    <ModalDelete :modalDelete="modalDelete" @message="removeHandler"></ModalDelete>


  </div>

</template>

<script>

import TitlePage from "@/components/utils/TitlePage";
import {mapGetters, mapMutations, mapActions} from 'vuex';
import ModalDelete from "@/components/layout/ModalDelete.vue";
import ItemActions from "@/components/layout/ItemActions.vue";
import CenterAutocomplete from "@/components/admin/CenterAutocomplete";
import SportsAutocompleteSingle from "@/components/admin/SportsAutocompleteSingle";
import CenterSelectSearch from "@/components/filters/CenterSelectSearch.vue";
import SportsSelectSearch from "@/components/filters/SportsSelectSearch.vue";
import DateRangeQuick from "@/components/filters/DateRangeQuick.vue";

const exportService = require('@/services/export')

export default {
  name: 'Bookings',
  components: {
    DateRangeQuick,
    SportsSelectSearch,
    CenterSelectSearch,
    CenterSelectSimple: CenterSelectSearch, ItemActions, ModalDelete, TitlePage, CenterAutocomplete,SportsAutocompleteSingle},
  data() {
    return {
      pathCreate: '/bookings/create/',
      pathEdit: '/bookings/edit/',
      selected: [],
      baseHeaders: [
        // { text: this.$t('Id'), align: 'start', value: '_id'},
        {text: this.$t('code'), align: 'start', value: 'code'},
        {text: this.$t('customer'), align: 'start', value: 'customer', sortable: false},
        {text: this.$t('sportsCenter'), value: 'center', sortable: false},
        {text: this.$t('field'), value: 'field', sortable: false},
        {text: this.$t('sports'), value: 'sport', sortable: false},
        {text: this.$t('status'), value: 'status', sortable: false},
        {text: this.$t('amount'), value: 'price', sortable: false},
        {text: this.$t('date'), value: 'date'},
        {text: this.$t('creation_date'), value: 'created_at'},
        {text: this.$t('actions'), align: 'start', value: 'action', sortable: false},
      ],
      items: [],
      items_count: 0,
      ids_to_delete: [],
      modalDelete: false,
      typeDelete: '',
      statuses: [
        {text: "", value: ""},
        {text: "Prenotata", value: "booked"},
        {text: "Completata", value: "completed"},
        {text: "Da pagare", value: "to_be_payed"},
        {text: "Cancellata", value: "canceled"},
        {text: "Rimborsata", value: "refunded"},
        {text: "Interrotta", value: "interrupted"},
        {text: "In Attesa", value: "pending"},
        {text: "Da Confermare", value: "to_be_confirmed"},
      ],
      filter: {},
      options_pagination: {},
      items_for_page:[10, 20, 25, 50, 75, 100],
      createdFilter: { from: null, to: null },
      bookingFilter: { from: null, to: null },
      datePreset: null,
      promoItems: [
        { text: '', value: null },
        { text: 'Sì',    value: true },
        { text: 'No',    value: false },
      ],
      _aborter: null,
      _debouncedSearch: null,
      showAdvanced: false,
      onCreate: false
    };
  },
  async created() {
    this.onCreate = true
    this.filter = Object.assign({}, this.getFilter, {
      customer_q:   this.getFilter && this.getFilter.customer_q ? this.getFilter.customer_q : null,
      created_from: this.getFilter && this.getFilter.created_from ? this.getFilter.created_from : null,
      created_to:   this.getFilter && this.getFilter.created_to   ? this.getFilter.created_to   : null,
      booking_from: this.getFilter && this.getFilter.booking_from ? this.getFilter.booking_from : null,
      booking_to:   this.getFilter && this.getFilter.booking_to   ? this.getFilter.booking_to   : null,
    });
    this.options_pagination = Object.assign({},this.getOptionsPagination)

    if (!this.filter.created_from && !this.filter.created_to &&
        !this.filter.booking_from && !this.filter.booking_to) {
      this.applyPreset(this.datePreset);
    }

    if(this.$route.query.user_id && this.getUser.super_admin){
      this.filter.user_id = this.$route.query.user_id
    }else {
      delete this.filter.user_id
    }
    if(this.$route.query.center_id && this.getUser.super_admin){
      this.filter.center_id = this.$route.query.center_id
    }else {
      delete this.filter.center_id
    }
    if(this.$route.query.field_id && this.getUser.super_admin){
      this.filter.field_id = this.$route.query.field_id
    }else {
      delete this.filter.field_id
    }

    if(this.$route.query.promo_sa_global && this.getUser.super_admin){
      this.filter.promo_sa_global = (this.$route.query.promo_sa_global === 'true')
    }else {
      delete this.filter.promo_sa_global
    }

    if(this.$route.query.status){
      this.filter.status = this.$route.query.status
    }else {
      delete this.filter.status
    }

    this._debouncedSearch = this.debounce(async () => {
      await this.makeSearch(true);
    }, 350);
    this.onCreate = false

  },
  watch: {
    'filter.code':           function () { !this.onCreate && this._debouncedSearch(); },
    'filter.sport_id':       function () { !this.onCreate && this._debouncedSearch(); },
    'filter.status':         function () { !this.onCreate && this._debouncedSearch(); },
    'filter.center_id':      function () { !this.onCreate && this._debouncedSearch(); },
    'filter.field_id':       function () { !this.onCreate && this._debouncedSearch(); },
    'filter.promo_sa_global':function () { !this.onCreate && this._debouncedSearch(); },
    'filter.customer_q':     function () { !this.onCreate && this._debouncedSearch(); },
    datePreset: function (val) { !this.onCreate && this.applyPreset(val); },
  },
  computed: {
    ...mapGetters({
      'getUser': 'user/getUser',
      'getFilter':'bookings/getFilter',
      'getOptionsPagination':'bookings/getOptionsPagination',
    }),
    isSuperAdmin(){
      return (this.getUser && this.getUser.super_admin) ? this.getUser.super_admin : false;
    },
    headers() {
      if (!this.isSuperAdmin) return this.baseHeaders;

      let promoHeader = { text: 'Promo SA', value: 'promo_sa_global' };
      let headers = this.baseHeaders.slice();

      let dateIndex = headers.findIndex(function (h) { return h && h.value === 'date'; });
      if (dateIndex >= 0) headers.splice(dateIndex + 1, 0, promoHeader);
      else headers.splice(headers.length - 2, 0, promoHeader); // fallback

      return headers;
    }
  },
  methods: {
    ...mapMutations({
      'setLoading': 'settings/setLoading',
      'setNotification': 'settings/setNotification',
    }),

    ...mapActions({
      'getItems': 'bookings/index',
      'deleteItem': 'bookings/delete',
      'massiveDeleteItem': 'bookings/massDelete',
      'setFilter': 'bookings/setFilter',
      'setOptionsPagination': 'bookings/setOptionsPagination',
    }),

    onCreatedChange(payload){
      this.filter.created_from = payload.from;
      this.filter.created_to   = payload.to;
      if (this._debouncedSearch) this._debouncedSearch(); else this.makeSearch(true);
    },

    onBookingChange(payload){
      this.filter.booking_from = payload.from;
      this.filter.booking_to   = payload.to;
      if (this._debouncedSearch) this._debouncedSearch(); else this.makeSearch(true);
    },

    debounce: function (fn, wait) {
      var t = null;
      return function () {
        var ctx = this, args = arguments;
        clearTimeout(t);
        t = setTimeout(function(){ fn.apply(ctx, args); }, wait);
      };
    },

    formatRangeLabel: function (from, to) {
      function fmt(d) {
        if (!d) return '—';
        var date = new Date(d);
        if (isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('it-IT');
      }
      return fmt(from) + ' → ' + fmt(to);
    },

    atStartOfDayISO: function (d) {
      var dt = new Date(d); dt.setHours(0,0,0,0); return dt.toISOString();
    },
    atEndOfDayISO: function (d) {
      var dt = new Date(d); dt.setHours(23,59,59,999); return dt.toISOString();
    },

    applyPreset: function (val) {
      if (val === 'custom') return;

      var now = new Date();
      var to = this.atEndOfDayISO(now);
      var from;

      if (val === 'ultimi_30') {
        var d30 = new Date(now); d30.setDate(d30.getDate() - 30);
        from = this.atStartOfDayISO(d30);
      }
      if (val === 'ultima_sett') {
        var d7 = new Date(now); d7.setDate(d7.getDate() - 7);
        from = this.atStartOfDayISO(d7);
      }
      if (val === 'ultimo_mese') {
        var firstPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        var lastPrev  = new Date(now.getFullYear(), now.getMonth(), 0);
        from = this.atStartOfDayISO(firstPrev);
        to   = this.atEndOfDayISO(lastPrev);
      }

      if(!val){
        from = null
        to   = null
      }

      this.filter.created_from = from;
      this.filter.created_to   = to;
      this.filter.booking_from = from;
      this.filter.booking_to   = to;

      this.createdFilter = { from, to };
      this.bookingFilter = { from, to };
    },

    applyCreatedRange: function () {
      var f = this.createdRange && this.createdRange.from;
      var t = this.createdRange && this.createdRange.to;
      if (f && t) {
        this.filter.created_from = this.atStartOfDayISO(f);
        this.filter.created_to   = this.atEndOfDayISO(t);
        this.menuCreated = false;
        this.datePreset = 'custom';
        this._debouncedSearch();
      }
    },

    applyBookingRange: function () {
      var f = this.bookingRange && this.bookingRange.from;
      var t = this.bookingRange && this.bookingRange.to;
      if (f && t) {
        this.filter.booking_from = this.atStartOfDayISO(f);
        this.filter.booking_to   = this.atEndOfDayISO(t);
        this.menuBooking = false;
        this.datePreset = 'custom';
        this._debouncedSearch();
      }
    },

    hardReset: async function () {
      this.onCreate = true;
      var filters = Object.assign({}, this.getFilter);
      for (var k in filters) { filters[k] = null; }

      filters.customer_q    = null;
      filters.created_from  = null;
      filters.created_to    = null;
      filters.booking_from  = null;
      filters.booking_to    = null;

      this.filter = Object.assign({}, filters);

      this.createdFilter = { from: null, to: null };
      this.bookingFilter = { from: null, to: null };

      this.options_pagination = { page: 1, limit: 10 };

      this.datePreset = null;
      this.applyPreset(this.datePreset);
      this.onCreate = false;

      await this.makeSearch(true);
    },

    async resetFilter(){
      var filters = Object.assign({},this.getFilter)
      for (const property in filters) {
        filters[property] = null
      }
      this.filter = Object.assign({}, filters)
      this.options_pagination = Object.assign({},{
        page: 1,
        limit: 10
      })
      await this.makeSearch()
    },
    async makeSearch(reset = null) {
      this.setLoading(true);

      if(reset){
        this.options_pagination.page = 1
      }
      await this.setFilter(this.filter)
      await this.setOptionsPagination(this.options_pagination)

      if (this._aborter && this._aborter.abort) { try { this._aborter.abort(); } catch(e){} }
      this._aborter = {};

      var results = await this.getItems();

      this.items = results.results
      this.items_count = results.count
      this.setLoading(false);
    },

    async actionHandler(type) {
      switch (type) {
        case "edit":
          await this.edit(this.selected[0]._id)
          break;
        case "massiveDelete":
          await this.confirmMassiveRemove()
          break;
        case "export":
          await this.export()
          break;
        default:
          break
      }
    },

    edit(id) {
      this.$router.push(this.pathEdit + id)
    },

    confirmRemove(id) {
      this.ids_to_delete = [id]
      this.modalDelete = true
      this.typeDelete = 'single'
    },

    confirmMassiveRemove() {
      this.ids_to_delete = this.selected.map(x => x._id)
      this.modalDelete = true
      this.typeDelete = 'massive'
    },

    async removeHandler(mode) {
      if (this.typeDelete == 'single') {
        await this.remove(mode)
      } else if (this.typeDelete == 'massive') {
        await this.massiveRemove(mode)
      } else {
        this.modalDelete = false
        this.ids_to_delete = []
        this.typeDelete = ''
      }
    },

    async remove(mode) {

      if (!mode) {

        this.modalDelete = false

      } else {

        this.modalDelete = false
        this.setLoading(true);

        var result = await this.deleteItem({id: this.ids_to_delete[0]})

        if (result.success) {

          this.setNotification({
            active: true,
            color: 'success',
            mode: 'multi-line',
            text: this.$t('deleteSuccess')
          })

          var results = await this.getItems({filter: this.filter, pagination: this.options_pagination});
          this.items = results.results
          this.items_count = results.count

          this.setLoading(false);

        } else {

          this.setNotification({
            active: true,
            color: 'error',
            mode: 'multi-line',
            text: result
          })

          this.setLoading(false);
        }
      }

    },
    async massiveRemove(mode) {
      if (!mode) {

        this.modalDelete = false

      } else {

        this.modalDelete = false
        this.setLoading(true);


        var result = await this.massiveDeleteItem({_id: {$in: this.ids_to_delete}})

        if (result.success) {

          this.setNotification({
            active: true,
            color: 'success',
            mode: 'multi-line',
            text: this.$t('deleteSuccess')
          })

          var results = await this.getItems({filter: this.filter, pagination: this.options_pagination});
          this.items = results.results
          this.items_count = results.count

          this.selected = []
          this.ids_to_delete = []

          this.setLoading(false);

        } else {

          this.setNotification({
            active: true,
            color: 'error',
            mode: 'multi-line',
            text: result
          })

          this.setLoading(false);
        }
      }
    },
    async getNewPage() {
      await this.makeSearch()
    },
    calculatePagination(count, limit) {

      var divide = (count / limit)
      var rest = divide % 1

      if (rest > 0) {
        divide = divide - rest
        divide = +divide.toFixed(0) + 1
      }

      return +divide
    },
    async setCenterId(id) {
      this.filter.center_id = id
    },
    async setSportId(sport){
      this.filter.sport_id = sport._id
    },
    getStatus(status, type) {
      switch (status) {
        case "completed":
          return (type == 'name') ? 'Completata' : 'success'
        case "booked":
          return (type == 'name') ? 'Prenotato' : 'primary'
        case "to_be_payed":
          return (type == 'name') ? 'Da Pagare' : 'info'
        case "canceled":
        case "refunded":
          return (type == 'name') ? 'Annullata' : 'error'
        case "interrupted":
          return (type == 'name') ? 'Interrotta' : 'warning'
        case "pending":
          return (type == 'name') ? 'In attesa' : 'info'
        case "to_be_confirmed":
          return (type == 'name') ? 'Da confermare' : 'warning'
        default:
          return (type == 'name') ? status : 'info'
      }
    },
    getSlot(slot_id) {
      if(!slot_id) return ''
      let split = slot_id.split(';')
      return `${split[0]}/${split[1]}/${split[2]} | ${split[3]} - ${split[4]}`
    },
    async export(){
      var csv = await exportService.getCsv(this.headers, this.items)
      var encodedUri = encodeURI(csv);
      var link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `export_${new Date().toISOString()}.csv`);
      document.body.appendChild(link); // Required for FF
      link.click();
    },
    clearChip(key) {
      if (key === 'created_range') {
        this.filter.created_from = null;
        this.filter.created_to   = null;
        this.createdRange = { from: null, to: null };
      } else if (key === 'booking_range') {
        this.filter.booking_from = null;
        this.filter.booking_to   = null;
        this.bookingRange = { from: null, to: null };
      } else {
        this.filter[key] = this.multiple && Array.isArray(this.filter[key]) ? [] : null;
        if (key === 'center_id') this.$emit && this.$emit('message', ''); // opzionale
      }
      if (this._debouncedSearch) this._debouncedSearch();
      else this.makeSearch && this.makeSearch(true);
    }
  },
  mounted() {

  },
}
</script>
